/* LouHairstylesModal — bank av 8 AI-genererade frisyrer som matchar
 * användarens feminin arketyp + Kibbe-typ. Per kort: "Se det på mig"-knapp
 * (try-on med uppladdad selfie). */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, X, Sparkles, Upload, Scissors, Camera, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HairstyleCard({ style, onTryOn }) {
  const [src, setSrc] = useState(style.ref_image_b64 ? `data:${style.ref_image_mime || 'image/png'};base64,${style.ref_image_b64}` : null);
  const [busy, setBusy] = useState(false);

  const fetchImage = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/experts/lou/hairstyles/${style.id}/image`, {}, {
        withCredentials: true, timeout: 60000,
      });
      if (r.data?.ref_image_b64) {
        setSrc(`data:${r.data.ref_image_mime || 'image/png'};base64,${r.data.ref_image_b64}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte hämta bild');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-rose-100 shadow-sm overflow-hidden flex flex-col" data-testid={`hair-card-${style.id}`}>
      <div className="aspect-square bg-rose-50 relative flex items-center justify-center">
        {src ? (
          <img src={src} alt={style.name} className="w-full h-full object-cover" />
        ) : busy ? (
          <Loader2 className="w-7 h-7 animate-spin text-rose-400" />
        ) : (
          <button onClick={fetchImage} data-testid={`hair-load-${style.id}`} className="px-2.5 py-1.5 rounded-full bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-bold flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Visa AI-bild
          </button>
        )}
      </div>
      <div className="p-2.5 flex-1 flex flex-col gap-1">
        <div className="font-extrabold text-sm text-rose-800">{style.name}</div>
        <div className="flex gap-1 flex-wrap">
          {style.length && <span className="text-[10px] bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded-full font-bold">{style.length}</span>}
          {style.texture && <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full font-bold">{style.texture}</span>}
          {style.care_level && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">vård: {style.care_level}</span>}
        </div>
        {style.vibe && <p className="text-[11px] text-gray-700 italic">{style.vibe}</p>}
        {style.why && <p className="text-[11px] text-rose-700"><b>Varför du:</b> {style.why}</p>}
        <button onClick={() => onTryOn(style)} data-testid={`hair-try-${style.id}`} className="mt-auto w-full py-1.5 rounded-xl bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600 text-white text-xs font-bold flex items-center justify-center gap-1">
          <Camera className="w-3 h-3" /> Se det på mig
        </button>
      </div>
    </div>
  );
}

function TryOnPanel({ style, onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const run = async () => {
    if (!file) { toast.error('Välj en selfie först 💄'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('style_id', style.id);
      fd.append('file', file);
      const r = await axios.post(`${API}/experts/lou/hairstyles/try-on`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResult(`data:${r.data.image_mime || 'image/png'};base64,${r.data.image_b64}`);
      toast.success('Klar 💖');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte rendera frisyr');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={busy ? undefined : onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-4 py-3 text-white flex justify-between items-center">
          <h3 className="font-extrabold text-sm flex items-center gap-1.5"><Scissors className="w-4 h-4" /> {style.name}</h3>
          <button onClick={onClose} disabled={busy} data-testid="hair-tryon-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {!result && (
            <>
              <div className="text-[11px] text-rose-700 italic">Ladda upp en framifrån-selfie (utan hatt). Lou byter ut din nuvarande frisyr mot <b>{style.name}</b>.</div>
              <label className="block">
                <div className="px-3 py-2 rounded-xl border-2 border-dashed border-rose-300 cursor-pointer hover:bg-rose-50 text-xs text-rose-700 flex items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5" /> {file ? 'Byt selfie' : 'Välj selfie'}
                </div>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} data-testid="hair-tryon-pick" className="hidden" />
              </label>
              {preview && <img src={preview} alt="" className="w-32 h-32 object-cover rounded-2xl border-2 border-rose-200 mx-auto" />}
              <button onClick={run} disabled={busy || !file} data-testid="hair-tryon-run" className="w-full py-2.5 rounded-2xl bg-gradient-to-r from-rose-500 via-pink-500 to-fuchsia-500 text-white font-extrabold text-sm disabled:opacity-50 flex items-center justify-center gap-1.5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy ? 'Lou stylar dig…' : 'Visa mig med denna frisyr'}
              </button>
            </>
          )}
          {result && (
            <>
              <img src={result} alt="med ny frisyr" className="w-full rounded-2xl border-2 border-rose-200" data-testid="hair-tryon-result" />
              <div className="flex gap-2">
                <a href={result} download={`lou-${style.id}.png`} className="flex-1 py-2 rounded-xl bg-white border-2 border-rose-300 text-rose-700 text-xs font-bold text-center">Spara bild</a>
                <button onClick={() => { setResult(null); setFile(null); setPreview(null); }} className="flex-1 py-2 rounded-xl bg-rose-500 text-white text-xs font-bold flex items-center justify-center gap-1"><RotateCw className="w-3 h-3" /> Annan bild</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LouHairstyles({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [tryOnStyle, setTryOnStyle] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/experts/lou/hairstyles`, { withCredentials: true });
      if (r.data?.has_styles) setData(r.data);
    } catch {/**/} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const regen = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/experts/lou/hairstyles/generate`, {}, {
        withCredentials: true, timeout: 60000,
      });
      setData(r.data);
      toast.success('8 frisyrer klara 💇‍♀️');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte generera frisyrer');
    } finally { setGenerating(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-gradient-to-r from-pink-400 to-rose-500 px-5 py-3 flex justify-between items-center text-white">
          <h3 className="font-extrabold flex items-center gap-2"><Scissors className="w-4 h-4" /> Lou — frisyrer för dig</h3>
          <button onClick={onClose} data-testid="hair-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-7 h-7 animate-spin text-rose-400 mx-auto" /></div>
          ) : !data ? (
            <div className="text-center space-y-3 py-4">
              <div className="text-sm text-rose-700">Du har ingen frisyrbank än. Lou bygger 8 förslag baserade på din stil-analys (gör stil-analysen först om du inte har den).</div>
              <button onClick={regen} disabled={generating} data-testid="hair-generate" className="px-4 py-2.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-500 to-fuchsia-500 text-white font-extrabold text-sm disabled:opacity-50 flex items-center justify-center gap-2 mx-auto">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generating ? 'Lou tänker…' : 'Generera 8 frisyrer'}
              </button>
            </div>
          ) : (
            <>
              <div className="text-[11px] text-gray-600 flex justify-between items-center">
                <span>Baserat på: <b className="text-rose-700">{data.based_on?.archetype}</b> / <b className="text-rose-700">{data.based_on?.kibbe}</b>{data.based_on?.season ? ` · ${data.based_on.season}` : ''}</span>
                <button onClick={regen} disabled={generating} data-testid="hair-regen" className="text-rose-600 hover:text-rose-800 underline disabled:opacity-50">
                  {generating ? 'Genererar…' : 'Generera om'}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="hair-grid">
                {(data.styles || []).map((s) => (
                  <HairstyleCard key={s.id} style={s} onTryOn={setTryOnStyle} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {tryOnStyle && <TryOnPanel style={tryOnStyle} onClose={() => setTryOnStyle(null)} />}
    </div>
  );
}
